import React, { useState, useEffect } from "react";
import { getResolvedMenuItemAddonsAPI } from "../../redux/api";
import { SectionLoader } from "../common/Loader";
import "./ItemCustomizationModal.css";

const ItemCustomizationModal = ({
  isOpen,
  onClose,
  item,
  tabId,
  tableName,
  onAddToCart,
}) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]); // Array of addonItem objects
  const [addonGroups, setAddonGroups] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);

  // Active Size options
  const activeSizes =
    item?.menuSizeOptions?.filter((s) => s.isActive !== false) || [];

  // Reset & Load Initial Defaults
  useEffect(() => {
    if (isOpen && item) {
      // Default lowest size select
      if (activeSizes.length > 0) {
        const lowest = activeSizes.reduce(
          (prev, curr) => (curr.price < prev.price ? curr : prev),
          activeSizes[0]
        );
        setSelectedSize(lowest);
      } else {
        setSelectedSize(null);
      }
      setSelectedAddons([]);

      // Fetch Resolved Add-ons for this dish
      if (item.menuId) {
        setLoadingAddons(true);
        getResolvedMenuItemAddonsAPI(item.menuId)
          .then((data) => setAddonGroups(data || []))
          .catch(() => setAddonGroups([]))
          .finally(() => setLoadingAddons(false));
      }
    }
  }, [isOpen, item]);

  if (!isOpen || !item) return null;

  // Toggle Addon Checkbox respecting maxSelection
  const handleToggleAddon = (group, addonItem) => {
    const isAlreadySelected = selectedAddons.some(
      (a) => a.addonItemId === addonItem.addonItemId
    );

    if (isAlreadySelected) {
      setSelectedAddons((prev) =>
        prev.filter((a) => a.addonItemId !== addonItem.addonItemId)
      );
    } else {
      // Check group maxSelection limit
      const currentGroupCount = selectedAddons.filter(
        (a) => a.addonGroupId === group.addonGroupId
      ).length;

      if (group.maxSelection === 1) {
        // Radio behavior for maxSelection = 1: replace existing from same group
        const withoutThisGroup = selectedAddons.filter(
          (a) => a.addonGroupId !== group.addonGroupId
        );
        setSelectedAddons([...withoutThisGroup, { ...addonItem, addonGroupId: group.addonGroupId }]);
      } else if (currentGroupCount < (group.maxSelection || 99)) {
        setSelectedAddons((prev) => [
          ...prev,
          { ...addonItem, addonGroupId: group.addonGroupId },
        ]);
      }
    }
  };

  // Live Total Price Calculation
  const basePrice = selectedSize ? Number(selectedSize.price) : Number(item.price || 0);
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + (Number(a.price) || 0), 0);
  const totalItemPrice = basePrice + addonsTotal;

  const handleConfirm = () => {
    onAddToCart({
      menuId: item.menuId,
      foodName: item.foodName || item.name,
      selectedTab: tabId,
      tableName: tableName,
      selectedSize: selectedSize,
      selectedAddons: selectedAddons,
    });
    onClose();
  };

  return (
    <div className="custom-modal-overlay" onClick={onClose}>
      <div className="custom-modal-box" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="custom-modal-header">
          <div>
            <h3 style={{ margin: 0 }}>{item.foodName || item.name}</h3>
            <span style={{ fontSize: "12px", color: "var(--ui-muted)" }}>
              Customise as per preference
            </span>
          </div>
          <button className="custom-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="custom-modal-body">
          {/* 1. Size Options */}
          {activeSizes.length > 0 && (
            <div className="custom-section">
              <h4 className="custom-section-title">Select Quantity / Size</h4>
              <div className="custom-options-list">
                {activeSizes.map((size) => (     
<label
  key={size.sizeOptionId}
  className={`custom-option-row ${selectedSize?.sizeOptionId === size.sizeOptionId ? "active" : ""}`}
>
  <div className="custom-option-left">
    <input
      type="radio"
      name="sizeOption"
      checked={selectedSize?.sizeOptionId === size.sizeOptionId}
      onChange={() => setSelectedSize(size)}
    />
    <span className="custom-option-name">{size.sizeName}</span>
  </div>
  <span className="custom-option-price">₹{size.price}</span>
</label>
                ))}
              </div>
            </div>
          )}

          {/* 2. Add-on Groups */}
          {loadingAddons ? (
            <SectionLoader label="Loading add-ons..." rows={2} compact />
          ) : (
            addonGroups.map((group) => (
              <div key={group.addonGroupId} className="custom-section">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <h4 className="custom-section-title" style={{ margin: 0 }}>
                    {group.groupName}
                  </h4>
                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                    {group.maxSelection === 1 ? "Choose 1" : `Up to ${group.maxSelection}`}
                  </span>
                </div>
                <div className="custom-options-list">
                  {group.addonItems?.map((addon) => {
                    const isChecked = selectedAddons.some(
                      (a) => a.addonItemId === addon.addonItemId
                    );
                    return (
                 
<label
  key={addon.addonItemId}
  className={`custom-option-row ${isChecked ? "active" : ""}`}
>
  <div className="custom-option-left">
    <input
      type={group.maxSelection === 1 ? "radio" : "checkbox"}
      name={`group_${group.addonGroupId}`}
      checked={isChecked}
      onChange={() => handleToggleAddon(group, addon)}
    />
    <span className="custom-option-name">{addon.itemName}</span>
  </div>
  <span className="custom-option-price">+ ₹{addon.price}</span>
</label>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer with Live Price & Add Button */}
        <div className="custom-modal-footer">
          <button className="custom-add-btn" onClick={handleConfirm}>
            <span>Add Item</span>
            <span>₹{totalItemPrice}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemCustomizationModal;
